import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import { createEntity, editEntity, getStaff } from "src/api/settings";
import PermissionWrapper from "src/components/PermissionWrapper";
import { SelectField, TextField } from "src/components/form";
import { Button } from "src/components/ui/button";
import { DialogClose } from "src/components/ui/dialog";
import { usePortalContext } from "src/context/PortalContext";
import { showSuccessToast } from "src/lib/utils";
import type { EntityRecord } from "src/types/settings";

const fetchActiveStaff = getStaff("active");

const validationSchema = Yup.object().shape({
	name: Yup.string().required("This field is required"),
	address: Yup.string().required("This field is required"),
	type: Yup.string().required("This field is required"),
	manager_id: Yup.string(),
});

interface EntityFormValues {
	uuid?: string;
	name: string;
	address: string;
	manager_id: string;
	type: string;
}

const typeOptions = [
	{ label: "Store", value: "store" },
	{ label: "Warehouse", value: "warehouse" },
];

interface CreateWarehouseProps {
	/** Only supplied when editing an existing warehouse/storefront. */
	entity?: EntityRecord;
}

const CreateWarehouse = ({ entity }: CreateWarehouseProps) => {
	const queryClient = useQueryClient();
	const closePortal = usePortalContext();

	const initialValues: EntityFormValues = {
		...(entity && { uuid: entity.uuid }),
		name: entity?.name ?? "",
		address: entity?.address ?? "",
		manager_id: String(entity?.manager_id ?? ""),
		type: entity?.type ?? "",
	};

	const { mutate, isLoading } = useMutation(entity ? editEntity : createEntity, {
		onSuccess: ({ message }) => {
			showSuccessToast(message ?? "");
			queryClient.invalidateQueries(["entities"]);
			closePortal();
		},
	});

	const { data } = useQuery({
		queryKey: ["teams", "active"],
		queryFn: () => fetchActiveStaff({ page: 1, limit: 50 }),
		suspense: true,
	});

	const managerOptions = (data?.data ?? []).map(({ id, first_name, last_name }) => ({
		value: String(id),
		label: `${first_name ?? ""} ${last_name ?? ""}`.trim() || "Unnamed",
	}));

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ handleSubmit, values, handleChange, setFieldValue, errors, touched }) => (
				<form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-gap-4">
					<TextField
						label="Store name"
						name="name"
						placeholder="Store name"
						value={values.name}
						error={touched.name && errors.name}
						onChange={handleChange}
						required
					/>

					<TextField
						label="Store address"
						name="address"
						placeholder="Store address"
						value={values.address}
						error={touched.address && errors.address}
						onChange={handleChange}
						required
					/>

					<SelectField
						label="Type"
						name="type"
						placeholder="Select a type"
						value={values.type}
						error={touched.type && errors.type}
						onChange={(value) => setFieldValue("type", value)}
						options={typeOptions}
						required
					/>

					<PermissionWrapper permission={[["assign_warehouse_manager"], ["change_warehouse_manager"]]}>
						<SelectField
							label="Storefront manager"
							name="manager_id"
							placeholder="Select a manager"
							value={values.manager_id}
							error={touched.manager_id && errors.manager_id}
							onChange={(value) => setFieldValue("manager_id", value)}
							options={managerOptions}
							emptyMessage="No active team members"
						/>
					</PermissionWrapper>

					<div className="tw-mt-2 tw-flex tw-items-center tw-justify-end tw-gap-3">
						<DialogClose asChild>
							<Button type="button" variant="ghost">
								Cancel
							</Button>
						</DialogClose>
						<Button type="submit" variant="secondary" disabled={isLoading}>
							{isLoading ? "Saving..." : "Save"}
						</Button>
					</div>
				</form>
			)}
		</Formik>
	);
};

export default CreateWarehouse;
